export interface LiveHeaderStripProps {
  /** Eligible-event count for this user — post territory blocking, pre category filter, short markets included. */
  count: number;
}

/**
 * The Live page title strip (Figma "live page"): the red LIVE wordmark, the
 * "N Live Now" count, and a pulsing radar graphic bleeding off the right
 * edge. Renders even at count 0 (the all-empty state keeps the header).
 */
export function LiveHeaderStrip({ count }: LiveHeaderStripProps) {
  return (
    <div className="relative w-full overflow-hidden rounded-2xl bg-gradient-to-r from-danger/10 via-danger/5 to-transparent">
      <div className="flex items-center gap-3.5 px-6 py-3">
        <span className="text-[26px] font-extrabold italic tracking-tight text-danger">LIVE</span>
        <span className="text-sm font-medium text-ink-muted">{count} Live Now</span>
      </div>
      {/* On-air indicator per Figma (group 2147206784): a solid red dot inside
          soft FILLED concentric discs fading outward — a recording lamp, not a
          radiating radar. A brightness/scale swell travels dot → outer disc
          (see .live-lamp in index.css), so the lamp ripples like a wave while
          every disc stays in place. */}
      <div aria-hidden className="live-lamp pointer-events-none absolute right-10 top-1/2 -translate-y-1/2">
        <span
          className="lamp-disc absolute -inset-[30px] rounded-full bg-danger/[0.07]"
          style={{ '--wave-i': 4 } as React.CSSProperties}
        />
        <span
          className="lamp-disc absolute -inset-[21px] rounded-full bg-danger/10"
          style={{ '--wave-i': 3 } as React.CSSProperties}
        />
        <span
          className="lamp-disc absolute -inset-[13px] rounded-full bg-danger/15"
          style={{ '--wave-i': 2 } as React.CSSProperties}
        />
        <span
          className="lamp-disc absolute -inset-[6px] rounded-full bg-danger/25"
          style={{ '--wave-i': 1 } as React.CSSProperties}
        />
        <span className="lamp-disc block size-3 rounded-full bg-danger" style={{ '--wave-i': 0 } as React.CSSProperties} />
      </div>
    </div>
  );
}
